import { IonButton, IonContent, IonPage, IonImg, IonIcon, IonMenuToggle, useIonToast } from '@ionic/react';
import { ellipsisVertical, informationCircleOutline, refreshCircleOutline, trashOutline } from 'ionicons/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { firstValueFrom } from 'rxjs';
import { IState } from 'appRedux/createStore';
import { CasinoReduxCommandCreator } from 'appRedux/actions/casinoCommandCreator';
import { CasinoService, ICloakEgmRow } from 'services/casino';
import { ICasinoSchema } from 'appRedux/models/casinoModels';
import { bustCloakCache } from 'lib/cloakEgmsCache';
import useSlotDetailHistory from 'globalServices/useSlotDetailHistory';

const SLOT_CHECK_CASINO_ID = 4;
const CLOAK_GROUP = 'slotcheck-enterprise-cloakTheEgms';

const PdMenuKebab = () => (
  <div className="cloak-egms__menu-fab">
    <IonMenuToggle menu="main">
      <IonButton fill="clear" className="cloak-egms__menu-btn" aria-label="Open menu">
        <IonIcon icon={ellipsisVertical} className="cloak-egms__menu-icon" />
      </IonButton>
    </IonMenuToggle>
  </div>
);

const ForbiddenCard = () => (
  <IonPage className="app-parent cloak-egms">
    <IonContent className="cloak-egms">
      <PdMenuKebab />
      <div className="cloak-egms__content">
        <div className="cloak-egms__breadcrumb-row">
          <IonMenuToggle menu="main">
            <IonButton fill="clear" className="cloak-egms__back" aria-label="Open menu">
              <IonImg src="assets/images/back-icon.svg" />
            </IonButton>
          </IonMenuToggle>
          <div className="cloak-egms__breadcrumb">
            <span>Cloak EGMs</span>
          </div>
        </div>
        <div className="cloak-egms__forbidden-card">
          <h2>403 · Cloak EGMs not enabled</h2>
          <p>
            Cloak EGMs requires the Cognito group <strong>{CLOAK_GROUP}</strong>. Ask ops to assign
            it for your account.
          </p>
        </div>
      </div>
    </IonContent>
  </IonPage>
);

function casinoLabel(c: ICasinoSchema | undefined, fallback: string): string {
  if (!c) return fallback;
  return c.shortName || c.displayName || c.casinoName || fallback;
}


function formatExpires(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function dash(value?: string | null): string {
  const s = (value ?? '').trim();
  return s || '—';
}

export const CloakEgmsPage = () => {
  const casinoSvc = useMemo(() => new CasinoService(), []);
  const dispatch = useDispatch();
  const casinoCommands = CasinoReduxCommandCreator(dispatch);
  const history = useHistory();
  const { addToSlotHistory } = useSlotDetailHistory();
  const [presentToast] = useIonToast();
  const groups =
    (useSelector<IState>((s) => s.app.persistedState.userInfo?.groups) as string[]) || [];
  const canAccess = groups.includes(CLOAK_GROUP);
  const currentCasinoId = useSelector<IState>(
    (s) => s.app.persistedState.casinoSchema?.kpCasinoPk ?? s.app.persistedState.casinoSchema?.casinoId
  ) as number | undefined;
  const appUserFk = useSelector<IState>((s) => s.app.persistedState.userInfo?.appUserPk) as string;
  const [casinoFilter, setCasinoFilter] = useState('');
  const [casinoOptions, setCasinoOptions] = useState<ICasinoSchema[]>([]);
  const [rows, setRows] = useState<ICloakEgmRow[]>([]);
  const [mnum, setMnum] = useState('');
  const [phase, setPhase] = useState<'idle' | 'loading' | 'ready'>('idle');
  const [cloakBusy, setCloakBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState<{ uuid: string; op: 'extend' | 'trash' } | null>(null);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const loadSeq = useRef(0);
  const presentToastRef = useRef(presentToast);
  presentToastRef.current = presentToast;

  const selectedCasino = useMemo(
    () => casinoOptions.find((c) => String(c.kpCasinoPk ?? c.casinoId) === String(casinoFilter)),
    [casinoOptions, casinoFilter]
  );
  const selectedLabel = casinoLabel(selectedCasino, casinoFilter);
  const hasCasino = Boolean(casinoFilter);

  const loadAdmin = useCallback(
    async (casinoId: string, opts?: { fromRefresh?: boolean }) => {
      if (!casinoId) {
        loadSeq.current += 1;
        setRows([]);
        setPhase('idle');
        return;
      }
      const seq = ++loadSeq.current;
      if (!opts?.fromRefresh) setPhase('loading');
      try {
        const res = await firstValueFrom(casinoSvc.GetCloakedEgmsAdmin(casinoId));
        if (seq !== loadSeq.current) return;
        const payload = res && typeof res === 'object' ? res : null;
        const rawRows = payload && Array.isArray(payload.rows) ? payload.rows : [];
        const now = Date.now();
        const next = rawRows.filter((r) => {
          if (!r || r.active === false) return false;
          if (!r.expirationDate) return true;
          const t = new Date(r.expirationDate).getTime();
          if (Number.isNaN(t)) return true;
          return t > now;
        });
        setRows(next);
        setPhase('ready');
      } catch (err: any) {
        if (seq !== loadSeq.current) return;
        setRows([]);
        setPhase('ready');
        const status = err?.response?.status || err?.status;
        if (status !== 403 && status !== 304) {
          presentToastRef
            .current({
              position: 'top',
              duration: 4000,
              color: 'danger',
              message: 'Failed to load cloaked EGMs.',
            })
            .catch(() => null);
        }
      }
    },
    [casinoSvc]
  );

  useEffect(() => {
    if (!canAccess) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await firstValueFrom(casinoSvc.GetAllCasinos());
        const payload = res && typeof res === 'object' ? res : null;
        const list = (payload?.DataSet || []).filter((c) => {
          if (!c.isactive || c.isComingSoon) return false;
          const id = Number(c.kpCasinoPk ?? c.casinoId);
          if (id === SLOT_CHECK_CASINO_ID) return false;
          const name = `${c.shortName || ''} ${c.displayName || ''} ${c.casinoName || ''}`.toLowerCase();
          if (name.includes('slot check') || name.includes('slotcheck')) return false;
          return true;
        });
        list.sort((a, b) =>
          (a.shortName || a.displayName || a.casinoName || '').localeCompare(
            b.shortName || b.displayName || b.casinoName || ''
          )
        );
        if (!cancelled) setCasinoOptions(list);
      } catch {
        if (!cancelled) setCasinoOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccess, casinoSvc]);

  useEffect(() => {
    if (!canAccess) return;
    if (!casinoFilter) {
      void loadAdmin('');
      return;
    }
    void loadAdmin(casinoFilter);
  }, [canAccess, casinoFilter, loadAdmin]);

  const onRefresh = async () => {
    if (!casinoFilter) return;
    setRefreshBusy(true);
    try {
      bustCloakCache(casinoFilter);
      await loadAdmin(casinoFilter, { fromRefresh: true });
    } finally {
      setRefreshBusy(false);
    }
  };

  const onCloak = async () => {
    const value = mnum.trim();
    if (!casinoFilter || !value || cloakBusy) return;
    setCloakBusy(true);
    try {
      await firstValueFrom(casinoSvc.CloakEgm({ casinoId: casinoFilter, mnum: value }));
      bustCloakCache(casinoFilter);
      setMnum('');
      await loadAdmin(casinoFilter, { fromRefresh: true });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to cloak EGM.';
      presentToastRef
        .current({
          position: 'top',
          duration: 4000,
          color: 'danger',
          message: String(msg),
        })
        .catch(() => null);
    } finally {
      setCloakBusy(false);
    }
  };

  const onExtend = async (row: ICloakEgmRow) => {
    if (!row?.uuid || rowBusy) return;
    setRowBusy({ uuid: row.uuid, op: 'extend' });
    try {
      await firstValueFrom(casinoSvc.ExtendCloakEgm({ uuid: row.uuid }));
      if (casinoFilter) bustCloakCache(casinoFilter);
      await loadAdmin(casinoFilter, { fromRefresh: true });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to extend cloak.';
      presentToastRef
        .current({
          position: 'top',
          duration: 4000,
          color: 'danger',
          message: String(msg),
        })
        .catch(() => null);
    } finally {
      setRowBusy(null);
    }
  };

  const onDetail = (row: ICloakEgmRow) => {
    const mnum = Number(row.mnum);
    if (!Number.isFinite(mnum)) return;
    const filterId = String(casinoFilter);
    const exploreId = String(currentCasinoId ?? '');
    const selectedIds = [selectedCasino?.casinoId, selectedCasino?.kpCasinoPk]
      .filter((v) => v !== undefined && v !== null && String(v) !== '')
      .map((v) => String(v));
    const sameCasino =
      Boolean(exploreId) && (exploreId === filterId || selectedIds.includes(exploreId));
    if (selectedCasino && filterId && !sameCasino) {
      casinoCommands.SetCasinoSchema(selectedCasino);
      if (appUserFk && selectedCasino.casinoId) {
        casinoSvc.UpdateRecentCasinoID(String(selectedCasino.casinoId), appUserFk).subscribe({
          error: () => null,
        });
      }
    }
    addToSlotHistory(mnum);
    history.push('/tabs/home');
  };

  const onTrash = async (row: ICloakEgmRow) => {
    if (!row?.uuid || rowBusy) return;
    setRowBusy({ uuid: row.uuid, op: 'trash' });
    try {
      await firstValueFrom(casinoSvc.UncloakEgm({ uuid: row.uuid }));
      if (casinoFilter) bustCloakCache(casinoFilter);
      await loadAdmin(casinoFilter, { fromRefresh: true });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to trash cloak row.';
      presentToastRef
        .current({
          position: 'top',
          duration: 4000,
          color: 'danger',
          message: String(msg),
        })
        .catch(() => null);
    } finally {
      setRowBusy(null);
    }
  };

  if (!canAccess) return <ForbiddenCard />;

  const cloakDisabled = !hasCasino || !mnum.trim() || cloakBusy;
  const refreshDisabled = !hasCasino || refreshBusy;

  return (
    <IonPage className="app-parent cloak-egms">
      <IonContent className="cloak-egms">
        <PdMenuKebab />
        <div className="cloak-egms__content">
          <div className="cloak-egms__breadcrumb-row">
            <IonMenuToggle menu="main">
              <IonButton fill="clear" className="cloak-egms__back" aria-label="Open menu">
                <IonImg src="assets/images/back-icon.svg" />
              </IonButton>
            </IonMenuToggle>
            <div className="cloak-egms__breadcrumb">
              <span>Cloak EGMs</span>
            </div>
          </div>

          <div className="cloak-egms__header">
            <div className="cloak-egms__title-row">
              <h1 className="cloak-egms__title">Cloak EGMs</h1>
            </div>
            <p className="cloak-egms__subtitle">
              Hide machines from the Explore drawer list · this casino only
            </p>
          </div>

          <div className="cloak-egms__filter-card">
            <div className="cloak-egms__filter-main">
              <div className="cloak-egms__field">
                <span className="cloak-egms__field-label">Casino</span>
                <select
                  className="cloak-egms__select"
                  value={casinoFilter}
                  onChange={(e) => setCasinoFilter(e.target.value)}
                  aria-label="Casino"
                >
                  <option value="">Select casino</option>
                  {casinoOptions.map((c) => {
                    const id = c.kpCasinoPk ?? c.casinoId;
                    return (
                      <option key={id} value={String(id)}>
                        {casinoLabel(c, String(id))}
                      </option>
                    );
                  })}
                </select>
              </div>
              <p className="cloak-egms__filter-hint">Single-select · no All · exclude Slot Check</p>
            </div>
            <button
              type="button"
              className="cloak-egms__refresh"
              disabled={refreshDisabled}
              onClick={() => void onRefresh()}
            >
              Refresh
            </button>
          </div>

          {hasCasino && (
            <div className="cloak-egms__add-card">
              <div className="cloak-egms__add-row">
                <div className="cloak-egms__field cloak-egms__field--mnum">
                  <span className="cloak-egms__field-label">mnum</span>
                  <input
                    className="cloak-egms__input"
                    value={mnum}
                    onChange={(e) => setMnum(e.target.value)}
                    placeholder="Paste mnum"
                    aria-label="mnum"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void onCloak();
                      }
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="cloak-egms__cloak-btn"
                  disabled={cloakDisabled}
                  onClick={() => void onCloak()}
                >
                  Cloak
                </button>
                <p className="cloak-egms__add-hint">
                  Writes casinoId from the filter. Sets expiration to now+7. Cannot cloak onto another property.
                </p>
              </div>
            </div>
          )}

          {!hasCasino && (
            <div className="cloak-egms__card cloak-egms__gate">
              <h3>Select a casino to load cloaked EGMs.</h3>
              <p className="cloak-egms__gate-copy cloak-egms__gate-copy--desktop">
                Catalog is single-select. No All. Nothing fetches until a property is chosen — then
                the table auto-loads. Add and Refresh stay disabled until then.
              </p>
              <p className="cloak-egms__gate-copy cloak-egms__gate-copy--phone">
                No All. No fetch until chosen. Add stays off.
              </p>
            </div>
          )}

          {hasCasino && phase === 'loading' && (
            <div className="cloak-egms__card cloak-egms__table-card">
              <div className="cloak-egms__skeleton" />
              <div className="cloak-egms__skeleton" />
              <div className="cloak-egms__skeleton" />
            </div>
          )}

          {hasCasino && phase === 'ready' && rows.length === 0 && (
            <div className="cloak-egms__card cloak-egms__empty">
              <h3>No cloaked EGMs for this casino.</h3>
              <p>
                Paste an mnum and Cloak to hide it from the Explore drawer list at {selectedLabel} for 7 days.
              </p>
            </div>
          )}

          {hasCasino && phase === 'ready' && rows.length > 0 && (
            <div className="cloak-egms__card cloak-egms__table-card">
              <div className="cloak-egms__table-head-block">
                <h2 className="cloak-egms__table-title">Cloaked at {selectedLabel}</h2>
                <p className="cloak-egms__table-sub">Currently cloaked only · name/theme fill from the Explore card · paste-mnum can be —</p>
              </div>
              <div className="cloak-egms__table-scroll">
                <table className="cloak-egms__table">
                  <thead className="cloak-egms__table-head">
                    <tr>
                      <th>mnum</th>
                      <th>Name</th>
                      <th>Theme</th>
                      <th>Expires</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.uuid || row.mnum}>
                        <td>
                          <span className="cloak-egms__mnum">{row.mnum}</span>
                          <span className="cloak-egms__mnum-date">{formatExpires(row.expirationDate)}</span>
                          <span className="cloak-egms__mnum-meta">
                            {dash(row.name)} · {dash(row.theme)}
                          </span>
                        </td>
                        <td className="cloak-egms__name">{dash(row.name)}</td>
                        <td className="cloak-egms__theme">{dash(row.theme)}</td>
                        <td className="cloak-egms__expires">{formatExpires(row.expirationDate)}</td>
                        <td className="cloak-egms__action">
                          <button
                            type="button"
                            className="cloak-egms__icon-btn cloak-egms__detail"
                            onClick={() => onDetail(row)}
                            aria-label="Detail"
                            title="Open EGM detail"
                          >
                            <IonIcon icon={informationCircleOutline} />
                          </button>
                          <button
                            type="button"
                            className="cloak-egms__icon-btn cloak-egms__extend"
                            disabled={rowBusy?.uuid === row.uuid}
                            onClick={() => void onExtend(row)}
                            aria-label="Extend"
                            title="renew +7 days from today"
                          >
                            <IonIcon icon={refreshCircleOutline} />
                          </button>
                          <button
                            type="button"
                            className="cloak-egms__icon-btn cloak-egms__trash"
                            disabled={rowBusy?.uuid === row.uuid}
                            onClick={() => void onTrash(row)}
                            aria-label="Trash"
                            title="Deletes the cloak row, not the floor EGM."
                          >
                            <IonIcon icon={trashOutline} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="cloak-egms__table-foot">
                Detail opens the same EGM detail as View Details (this casino + mnum). Name/theme fill when Cloaked from the card. Paste-mnum can be —. Extend renews +7 from today. Trash deletes the cloak row.
              </p>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default CloakEgmsPage;
